<!-- components/DevicePicker.vue -->
<template>
	<div class="mask" @click.self="$emit('close')">
		<div class="panel">
			<!-- ───── header ───── -->
			<header>
				<h2>{{ headerLabel }}</h2>
				<button class="x" @click="$emit('close')">×</button>
			</header>

			<!-- ───── elenco device ───── -->
			<ul v-if="!loading">
				<li v-for="d in devices" :key="d.id">
					<button @click="selectDevice(d.id)">
						<strong>#{{ d.id }}</strong> · {{ d.name || "— senza nome —" }}
					</button>
				</li>
			</ul>

			<p v-else class="loading">Caricamento…</p>
			<p v-if="error" class="error">
				{{ error }}
			</p>

			<!-- ───── crea nuovo ───── -->
			<footer v-if="canCreate">
				<button class="create" @click="createDevice">+ Nuovo device</button>
			</footer>
		</div>
	</div>
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import { API_URL } from "@/config";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";

/* quale pagina aprire dopo la scelta / creazione */
const props = defineProps<{ target: "Editor" | "Data" }>();
const headerLabel =
	props.target === "Editor" ? "Apri in Editor" : "Apri in Data";
/* flag per creazione nuovo device solo se si clicca Editor */
const canCreate = props.target === "Editor";

const emit = defineEmits(["close"]);
const router = useRouter();
const toast = useToast();

const devices = ref<any[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

/* ─── carica lista esistente ─── */
async function loadDevices() {
	loading.value = true;
	try {
		const { data } = await axios.get("/devices/get-all-graphs", {
			withCredentials: true,
		});
		// mostriamo i devices filtrando i device archiviati (is_archived = true)
		devices.value = (data ?? []).filter((g: any) => g.is_archived === false);
	} catch (e: any) {
		error.value = e.response?.data?.details || "Errore caricamento";
	} finally {
		loading.value = false;
	}
}

onMounted(loadDevices);

/* ─── seleziona ─── */
function selectDevice(id: number) {
	emit("close");
	router.push({ name: props.target, params: { deviceId: id } });
}

/* ─── crea nuovo ─── */
async function createDevice() {
	if (!canCreate) return;

	try {
		const newDevice = await axios.post(`${API_URL}/devices/new-device`);
		toast.success(
			`Nuovo Device con id: ${newDevice.data.id} aggiunto con successo`,
			{
				position: "top-right",
				duration: 2000,
			},
		);

		emit("close"); // chiudi modal
		/* per un nuovo device ha senso andare subito in EDITOR:               *
		 * se invece vuoi rispettare il target basta sostituire props.target  */
		router.push({ name: "Editor", params: { deviceId: newDevice.data.id } });
	} catch {
		toast.error("Errore nella creazione", {
			position: "top-right",
			duration: 2000,
		});
	}
}
</script>

<style scoped>
/* stessa veste grafica di prima … (copia/ioni dei tuoi <style>) */
.mask {
	position: fixed;
	inset: 0;
	backdrop-filter: blur(3px);
	background: rgba(0, 0, 0, 0.55);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
}
.panel {
	width: 360px;
	max-width: 90%;
	background: #0d0d0d;
	color: #fff;
	padding: 24px;
	border-radius: 8px;
	box-shadow: 0 18px 40px rgba(0, 0, 0, 0.4);
	display: flex;
	flex-direction: column;
	gap: 18px;
}
header {
	display: flex;
	justify-content: space-between;
	align-items: center;
}
h2 {
	font: 600 18px/1 "Inter", sans-serif;
	margin: 0;
}
.x {
	background: none;
	border: 0;
	color: #fff;
	font-size: 24px;
	cursor: pointer;
}

ul {
	list-style: none;
	margin: 0;
	padding: 0;
	max-height: 260px;
	overflow: auto;
	display: flex;
	flex-direction: column;
	gap: 6px;
}
li button {
	width: 100%;
	text-align: left;
	padding: 8px 10px;
	border-radius: 4px;
	background: #191919;
	border: 1px solid #282828;
	color: #eee;
	cursor: pointer;
}
li button:hover {
	background: #222;
	border-color: #008f8f;
}

.loading,
.error {
	color: #bbb;
}
.error {
	color: #e74c3c;
}

footer {
	display: flex;
	justify-content: flex-end;
}
.create {
	padding: 8px 16px;
	background: #008f8f;
	color: #fff;
	border: 0;
	border-radius: 4px;
	font: 600 14px/1 "Inter", sans-serif;
	cursor: pointer;
}
.create:hover {
	background: #00a7a7;
}
.create:active {
	background: #007777;
}
</style>
